import type { WorkflowsConditionField } from "../../context/workflows-editor-config-context";
import { RETURN_TRIGGER_TYPE_OPTIONS } from "../trigger/constants";

/**
 * Condition fields of the return domain, consumed through the config
 * context (WorkflowsEditorConfig.conditionFields) by the return provider.
 *
 * Paths are dotted lookups on the engine's ReturnEventData
 * (apps/baback-end/src/modules/return-workflows) and are validated against
 * the Prisma ReturnRequest model. The evaluator does not traverse arrays of
 * objects, so per-line-item fields (line_items[].resolution…) are not
 * addressable — only scalars and string arrays are listed here.
 */

// The returnRequest is present on every event, so every field applies to
// all return triggers.
const ALL_RETURN_TRIGGERS = RETURN_TRIGGER_TYPE_OPTIONS;

export const returnConditionFields: ReadonlyArray<WorkflowsConditionField> = [
  {
    value: "returnRequest.shipping_info.carrier",
    label:
      "workflowsEditor.node.condition.fields.returnRequest.shippingInfo.carrier",
    defaultLabel: "Return carrier",
    operators: ["equals", "not_equals", "in", "not_in"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.lang",
    label: "workflowsEditor.node.condition.fields.returnRequest.lang",
    defaultLabel: "Customer language",
    operators: ["equals", "not_equals", "in", "not_in"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.shipping_address.country_code",
    label:
      "workflowsEditor.node.condition.fields.returnRequest.shippingAddress.countryCode",
    defaultLabel: "Customer country code",
    operators: ["equals", "not_equals", "in", "not_in"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.shipping_address.city",
    label:
      "workflowsEditor.node.condition.fields.returnRequest.shippingAddress.city",
    defaultLabel: "Customer city",
    operators: ["equals", "not_equals", "contains"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.customer.email",
    label:
      "workflowsEditor.node.condition.fields.returnRequest.customer.email",
    defaultLabel: "Customer email",
    operators: ["equals", "not_equals", "contains"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.customer.tags",
    label: "workflowsEditor.node.condition.fields.returnRequest.customer.tags",
    defaultLabel: "Customer tags",
    operators: ["array_contains_any", "array_contains_all"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.orderTags",
    label: "workflowsEditor.node.condition.fields.returnRequest.orderTags",
    defaultLabel: "Order tags",
    operators: ["array_contains_any", "array_contains_all"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.orderName",
    label: "workflowsEditor.node.condition.fields.returnRequest.orderName",
    defaultLabel: "Order name",
    operators: ["equals", "not_equals", "contains"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.financial_informations.refund.amount",
    label:
      "workflowsEditor.node.condition.fields.returnRequest.financialInformations.refund.amount",
    defaultLabel: "Refund amount",
    operators: ["equals", "greater_than", "less_than"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.status.approved.done",
    label:
      "workflowsEditor.node.condition.fields.returnRequest.status.approved.done",
    defaultLabel: "Return approved",
    operators: ["is_true", "is_false"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  // BAB-2875 — the two remaining discriminants of _resolveReturnCreatedEmail
  // (default-email variants), so the email defaults can be expressed as
  // workflows: an empty label_url means "no shipping label yet".
  {
    value: "returnRequest.shipping_info.label_url",
    label:
      "workflowsEditor.node.condition.fields.returnRequest.shippingInfo.labelUrl",
    defaultLabel: "Label URL",
    operators: ["equals", "not_equals"],
    triggers: ALL_RETURN_TRIGGERS,
  },
  {
    value: "returnRequest.status.approved.status",
    label:
      "workflowsEditor.node.condition.fields.returnRequest.status.approved.status",
    defaultLabel: "Approval status",
    operators: ["equals", "not_equals", "in", "not_in"],
    triggers: ALL_RETURN_TRIGGERS,
  },
] as const;
